import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Snackbar,
  Box,
  Chip,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AddIcon from "@material-ui/icons/Add";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import utils from "../../universal/utils";

const styles = (theme) => ({
  root: {
    width: "100%",
  },
  tableContainer: {
    maxHeight: 600,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  dialogContent: {
    minWidth: 600,
  },
  tireInfo: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 4,
  },
  tireLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  imageUploadBox: {
    border: `2px dashed ${theme.palette.grey[400]}`,
    borderRadius: 4,
    padding: theme.spacing(3),
    textAlign: "center",
    marginTop: theme.spacing(2),
    cursor: "pointer",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.grey[50],
    },
  },
  imagePreview: {
    maxWidth: "100%",
    maxHeight: 300,
    marginTop: theme.spacing(2),
    borderRadius: 4,
  },
  uploadButton: {
    marginTop: theme.spacing(1),
  },
  motorcycleChip: {
    margin: theme.spacing(0.5),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
  },
  stockChip: {
    fontWeight: 600,
  },
  inStockChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    fontWeight: 600,
  },
  outOfStockChip: {
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.common.white,
    fontWeight: 600,
  },
});

const hebrewLabels = {
  title: "ניהול סטי צמיגים",
  table: {
    brand: "מותג",
    model: "דגם",
    frontSize: "מידה קדמי",
    rearSize: "מידה אחורי",
    stock: "מלאי",
    motorcycles: "אופנועים",
    actions: "פעולות",
  },
  stock: {
    bothInStock: "שניהם במלאי",
    notBothInStock: "לא במלאי מלא",
  },
  buttons: {
    create: "צור סט",
    upload: "העלה תמונה",
    save: "שמור",
    cancel: "ביטול",
  },
  dialog: {
    title: "יצירת סט צמיגים",
    frontTire: "צמיג קדמי",
    rearTire: "צמיג אחורי",
    uploadImage: "העלה תמונה (אופציונלי)",
    clickToUpload: "לחץ להעלאת תמונה",
    imageSelected: "תמונה נבחרה",
  },
  messages: {
    loading: "טוען הזדמנויות...",
    creating: "יוצר סט...",
    noOpportunities: "לא נמצאו הזדמנויות ליצירת סטים",
    success: "הסט נוצר בהצלחה!",
    error: "שגיאה ביצירת הסט",
  },
};

const basePath = utils.getEdgeBasePath();

class TireSetsManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      opportunities: [],
      loading: true,
      dialogOpen: false,
      selectedOpportunity: null,
      selectedImage: null,
      selectedImagePreview: null,
      creating: false,
      snackbar: {
        open: false,
        message: "",
        severity: "success",
      },
    };
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.loadOpportunities();
  }

  loadOpportunities = async () => {
    try {
      this.setState({ loading: true });
      const response = await fetch(
        `${basePath}/api/v1/admin/tire-sets/opportunities`,
        { headers: utils.getAdminAuthHeaders() }
      );
      const data = await response.json();

      if (data.ok) {
        this.setState({
          opportunities: data.opportunities,
          loading: false,
        });
      } else {
        throw new Error(data.error || "Failed to load opportunities");
      }
    } catch (error) {
      console.error("Error loading opportunities:", error);
      this.showSnackbar(hebrewLabels.messages.error, "error");
      this.setState({ loading: false });
    }
  };

  handleCreateClick = (opportunity) => {
    this.setState({
      dialogOpen: true,
      selectedOpportunity: opportunity,
      selectedImage: null,
      selectedImagePreview: null,
    });
  };

  handleDialogClose = () => {
    this.setState({
      dialogOpen: false,
      selectedOpportunity: null,
      selectedImage: null,
      selectedImagePreview: null,
    });
  };

  handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        this.setState({
          selectedImage: reader.result, // base64
          selectedImagePreview: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  handleImageUploadClick = () => {
    this.fileInputRef.current.click();
  };

  handleCreateSet = async () => {
    const { selectedOpportunity, selectedImage } = this.state;
    
    if (!selectedOpportunity) return;

    try {
      this.setState({ creating: true });

      const requestBody = {
        frontEan: selectedOpportunity.front.ean,
        rearEan: selectedOpportunity.rear.ean,
      };

      if (selectedImage) {
        requestBody.image = selectedImage;
      }

      const response = await fetch(`${basePath}/api/v1/admin/tire-sets/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (data.ok) {
        this.showSnackbar(hebrewLabels.messages.success, "success");
        this.handleDialogClose();
        // Remove the created opportunity from the list
        this.setState((prevState) => ({
          opportunities: prevState.opportunities.filter(
            (opp) =>
              opp.front.ean !== selectedOpportunity.front.ean ||
              opp.rear.ean !== selectedOpportunity.rear.ean
          ),
        }));
      } else {
        throw new Error(data.error || "Failed to create set");
      }
    } catch (error) {
      console.error("Error creating set:", error);
      this.showSnackbar(`${hebrewLabels.messages.error}: ${error.message}`, "error");
    } finally {
      this.setState({ creating: false });
    }
  };

  showSnackbar = (message, severity = "success") => {
    this.setState({
      snackbar: {
        open: true,
        message,
        severity,
      },
    });
  };

  handleSnackbarClose = () => {
    this.setState({
      snackbar: {
        ...this.state.snackbar,
        open: false,
      },
    });
  };

  render() {
    const { classes } = this.props;
    const {
      opportunities,
      loading,
      dialogOpen,
      selectedOpportunity,
      selectedImagePreview,
      creating,
      snackbar,
    } = this.state;

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
          <Typography variant="body1" style={{ marginRight: 16 }}>
            {hebrewLabels.messages.loading}
          </Typography>
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{hebrewLabels.table.brand}</TableCell>
                <TableCell>{hebrewLabels.table.model}</TableCell>
                <TableCell>{hebrewLabels.table.frontSize}</TableCell>
                <TableCell>{hebrewLabels.table.rearSize}</TableCell>
                <TableCell>{hebrewLabels.table.stock}</TableCell>
                <TableCell>{hebrewLabels.table.motorcycles}</TableCell>
                <TableCell>{hebrewLabels.table.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {opportunities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className={classes.emptyState}>
                      <Typography variant="body1">
                        {hebrewLabels.messages.noOpportunities}
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                opportunities.map((opportunity, index) => (
                  <TableRow key={index}>
                    <TableCell>{opportunity.front.brand}</TableCell>
                    <TableCell>{opportunity.front.displayName}</TableCell>
                    <TableCell>{opportunity.front.displaySize}</TableCell>
                    <TableCell>{opportunity.rear.displaySize}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          opportunity.bothInStock
                            ? hebrewLabels.stock.bothInStock
                            : hebrewLabels.stock.notBothInStock
                        }
                        size="small"
                        className={
                          opportunity.bothInStock
                            ? classes.inStockChip
                            : classes.outOfStockChip
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${opportunity.motorcycleCount} אופנועים`}
                        size="small"
                        color="primary"
                        className={classes.motorcycleChip}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => this.handleCreateClick(opportunity)}
                      >
                        {hebrewLabels.buttons.create}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create Set Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={this.handleDialogClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{hebrewLabels.dialog.title}</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {selectedOpportunity && (
              <>
                <Box className={classes.tireInfo}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography className={classes.tireLabel}>
                      {hebrewLabels.dialog.frontTire}
                    </Typography>
                    <Chip
                      label={selectedOpportunity.front.inStock ? "במלאי" : "לא במלאי"}
                      size="small"
                      className={
                        selectedOpportunity.front.inStock
                          ? classes.inStockChip
                          : classes.outOfStockChip
                      }
                    />
                  </Box>
                  <Typography variant="body2">
                    {selectedOpportunity.front.brand} {selectedOpportunity.front.displayName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedOpportunity.front.displaySize} | EAN: {selectedOpportunity.front.ean}
                  </Typography>
                </Box>

                <Box className={classes.tireInfo}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography className={classes.tireLabel}>
                      {hebrewLabels.dialog.rearTire}
                    </Typography>
                    <Chip
                      label={selectedOpportunity.rear.inStock ? "במלאי" : "לא במלאי"}
                      size="small"
                      className={
                        selectedOpportunity.rear.inStock
                          ? classes.inStockChip
                          : classes.outOfStockChip
                      }
                    />
                  </Box>
                  <Typography variant="body2">
                    {selectedOpportunity.rear.brand} {selectedOpportunity.rear.displayName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedOpportunity.rear.displaySize} | EAN: {selectedOpportunity.rear.ean}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" gutterBottom>
                  {hebrewLabels.dialog.uploadImage}
                </Typography>
                <Box
                  className={classes.imageUploadBox}
                  onClick={this.handleImageUploadClick}
                >
                  <CloudUploadIcon fontSize="large" color="action" />
                  <Typography variant="body1" gutterBottom>
                    {selectedImagePreview
                      ? hebrewLabels.dialog.imageSelected
                      : hebrewLabels.dialog.clickToUpload}
                  </Typography>
                  <input
                    type="file"
                    ref={this.fileInputRef}
                    style={{ display: "none" }}
                    accept="image/*"
                    onChange={this.handleImageSelect}
                  />
                </Box>

                {selectedImagePreview && (
                  <Box textAlign="center">
                    <img
                      src={selectedImagePreview}
                      alt="Preview"
                      className={classes.imagePreview}
                    />
                  </Box>
                )}

                {selectedOpportunity.motorcycles && selectedOpportunity.motorcycles.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      אופנועים לדוגמה:
                    </Typography>
                    {selectedOpportunity.motorcycles.map((moto, idx) => (
                      <Chip
                        key={idx}
                        label={`${moto.brand} ${moto.model} (${moto.years || moto.year})`}
                        size="small"
                        className={classes.motorcycleChip}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleDialogClose} disabled={creating}>
              {hebrewLabels.buttons.cancel}
            </Button>
            <Button
              onClick={this.handleCreateSet}
              color="primary"
              variant="contained"
              disabled={creating}
              startIcon={creating ? <CircularProgress size={20} /> : <AddIcon />}
            >
              {creating ? hebrewLabels.messages.creating : hebrewLabels.buttons.save}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={this.handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={this.handleSnackbarClose} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

TireSetsManager.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(TireSetsManager);

